import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // verifying, success, already_verified, expired, error
    const [message, setMessage] = useState('');

    useEffect(() => {
        let isMounted = true;
        
        const verifyEmail = async () => {
            if (!token) return;
            
            try {
                const res = await axios.get(`${API_URL}/api/auth/verify-email/${token}`);
                if (isMounted) {
                    const data = res.data;
                    
                    if (data.verified) {
                        // Successfully verified (either now or already was)
                        if (data.already_verified) {
                            setStatus('already_verified');
                        } else {
                            setStatus('success');
                        }
                        setMessage(data.message);
                    } else if (data.expired) {
                        // Token expired
                        setStatus('expired');
                        setMessage(data.message);
                    } else if (data.already_used) {
                        // Link already used - but user might be verified
                        setStatus('already_used');
                        setMessage(data.message);
                    } else {
                        // Unknown state
                        setStatus('error');
                        setMessage(data.message || 'Unable to verify email.');
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setStatus('error');
                    setMessage(err.response?.data?.detail || 'Failed to verify email. Please try again.');
                }
            }
        };
        
        verifyEmail();
        
        return () => {
            isMounted = false;
        };
    }, [token]);

    const isSuccessState = status === 'success' || status === 'already_verified' || status === 'already_used';
    const showResendLink = status === 'expired' || status === 'error';

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/30">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    {status === 'verifying' && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                            </div>
                            <CardTitle>Verifying Your Email</CardTitle>
                            <CardDescription>Please wait while we verify your email address...</CardDescription>
                        </>
                    )}
                    
                    {status === 'success' && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                                <CheckCircle className="h-8 w-8 text-green-500" />
                            </div>
                            <CardTitle className="text-green-500">Email Verified!</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                    
                    {status === 'already_verified' && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                                <CheckCircle className="h-8 w-8 text-green-500" />
                            </div>
                            <CardTitle className="text-green-500">Already Verified!</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                    
                    {status === 'already_used' && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                                <AlertCircle className="h-8 w-8 text-amber-500" />
                            </div>
                            <CardTitle className="text-amber-500">Link Already Used</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                    
                    {status === 'expired' && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                                <AlertCircle className="h-8 w-8 text-amber-500" />
                            </div>
                            <CardTitle className="text-amber-500">Link Expired</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                    
                    {status === 'error' && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                                <XCircle className="h-8 w-8 text-red-500" />
                            </div>
                            <CardTitle className="text-red-500">Verification Failed</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </>
                    )}
                </CardHeader>
                
                <CardFooter className="flex flex-col gap-3">
                    {isSuccessState && (
                        <Button className="w-full" onClick={() => navigate('/login')}>
                            Continue to Login
                        </Button>
                    )}
                    
                    {showResendLink && (
                        <>
                            <Link to="/resend-verification" className="w-full">
                                <Button className="w-full" variant="default">
                                    Request New Verification Link
                                </Button>
                            </Link>
                            <Button className="w-full" variant="outline" onClick={() => navigate('/login')}>
                                Back to Login
                            </Button>
                        </>
                    )}
                </CardFooter>
            </Card>
        </div>
    );
}
